/**
 * Dynamic-block evaluator — main entry point.
 *
 * Given a BlockDefinition (optionally carrying dynamicMetadata) and an
 * INSERT instance payload (customProperties + visibilityState), produce
 * the resolved list of child entities for rendering / EXPLODE /
 * property-palette display.
 *
 * Pipeline (strict order):
 *   1. Overlay per-instance state onto the block metadata via
 *      `applyInstanceParameters` (from @dxf-ai-editor/drawing-model) —
 *      this writes currents (currentDistance / currentValue /
 *      currentStateId / currentPoint / currentX / currentY / currentAngle /
 *      currentLookupRowId) onto the parameter copies without mutating the
 *      shared metadata.
 *   2. Resolve parameter runtime values (distance / flipped / stateId /
 *      point / polar / xy / rotationAngle / lookupRowId).
 *   3. Run Lookup actions FIRST — they may cascade parameter updates via
 *      `runtimeValueUpdates`. When any Lookup emits updates, we apply
 *      them and re-resolve affected parameters, then proceed.
 *   4. Apply remaining (non-lookup) actions in evaluation order (graph
 *      edges → topological; falls back to declaration order when the
 *      graph is empty).
 *   5. Filter out entities hidden by the active visibility state.
 *
 * Static blocks (dynamicMetadata === undefined) pass through as a no-op,
 * matching pre-DYN-1 behavior exactly — existing callers see zero
 * regression.
 */

import type {
  BlockDefinition,
  DynamicAction,
  DynamicBlockMetadata,
  DynamicParameter,
} from "@dxf-ai-editor/drawing-model";
import { applyInstanceParameters } from "@dxf-ai-editor/drawing-model";

import { applyAction } from "./actions";
import type { ActionContext, RuntimeValueUpdate } from "./actions";
import {
  collectDimensionalConstraintsFromParameters,
  inferAnchorEntity,
} from "./constraintCollection";
import { applyConstraintStretch } from "./constraintStretch";
import type { EntityLike } from "./geometry";
import {
  resolveParameterRuntimeValue,
  type ParameterRuntimeValue,
} from "./parameters";
import { ARRAY_COPY_SOURCE_MARKER, filterVisibleEntities } from "./visibility";
import {
  solveBlockConstraints,
  type DimensionalConstraint,
  type GeometricConstraint,
} from "./constraintSolverIntegration";
import {
  evaluateFormulaBatch,
  type FormulaEntry,
  type FormulaScope,
  type FormulaValue,
} from "./formulaEvaluator";

// ═══════════════════════════════════════════════════════════════════
// Profiler sink — opt-in timing hooks (DYN-4.4)
// ═══════════════════════════════════════════════════════════════════
//
// profiler.ts installs a Collector on this module-level ref for the
// duration of an `evaluateBlockWithProfile` call. When absent, all of
// the hook calls are no-ops (zero overhead in production).

/** @internal — profile event sink contract, satisfied by profiler.ts. */
export interface ProfilerSink {
  actionStart(actionId: string): number;
  actionEnd(actionId: string, start: number): void;
  parameterStart(parameterId: string): number;
  parameterEnd(parameterId: string, start: number): void;
  solverReport(iterations: number, residualNorm: number): void;
  cacheMiss(): void;
  formulaStart(): number;
  formulaEnd(start: number): void;
}

let profilerSink: ProfilerSink | undefined;

export function setProfilerSink(sink: ProfilerSink | undefined): void {
  profilerSink = sink;
}

export function getProfilerSink(): ProfilerSink | undefined {
  return profilerSink;
}

export interface InsertInstanceLike {
  readonly customProperties?: Readonly<
    Record<string, string | number | boolean>
  >;
  readonly visibilityState?: string;
}

/** Maximum nesting depth for dynamic block INSERT chains (AutoCAD parity). */
export const MAX_NESTED_DYNAMIC_DEPTH = 10;

export interface EvaluateBlockInput {
  /**
   * Accept the structurally-relevant subset of BlockDefinition — keeps
   * the evaluator usable in tests and tools that construct block-like
   * payloads without going through the full drawing-model schema.
   */
  readonly blockDef: Pick<BlockDefinition, "name" | "entities"> & {
    readonly dynamicMetadata?: DynamicBlockMetadata;
  };
  readonly instance: InsertInstanceLike;
  /** Optional metadata override (used by tests / BlockRecord-first consumers). */
  readonly dynamicMetadata?: DynamicBlockMetadata;
  /**
   * DYN-3: parent block name chain — populated automatically by nested
   * evaluation. If the current blockDef.name is already present the
   * evaluator reports a cycle error and returns the static entity set.
   * External callers should NOT set this; it is used only by recursive
   * evaluation of nested INSERTs.
   */
  readonly parentChain?: readonly string[];
  /**
   * DYN-3 Batch C: map of every block in the drawing, keyed by block
   * name. Required for nested dynamic block evaluation — when a child
   * INSERT is encountered inside `blockDef.entities`, the evaluator
   * looks up the nested BlockDefinition here to recurse with the
   * child's per-instance customProperties / visibilityState.
   *
   * When omitted, nested INSERT children are emitted verbatim (pre-DYN-3
   * behaviour). Callers that want cascade evaluation supply the full
   * map from `document.blocks`.
   */
  readonly blockDefinitions?: Readonly<
    Record<
      string,
      Pick<BlockDefinition, "name" | "entities"> & {
        dynamicMetadata?: DynamicBlockMetadata;
      }
    >
  >;
}

export interface EvaluateBlockResult {
  readonly entities: ReadonlyArray<EntityLike & { readonly id: string }>;
  readonly runtimeValues: ReadonlyMap<string, ParameterRuntimeValue>;
  readonly errors: readonly string[];
  /**
   * Computed BTABLE property values (DYN-4.1). Keys are block property
   * entry keys; values are the formula-evaluation result or the static
   * default when no formula is defined. Missing entries signal a parse
   * or eval failure — the error is in `errors`.
   */
  readonly computedProperties: Readonly<Record<string, FormulaValue>>;
}

/* ── helpers ───────────────────────────────────────────────────── */

function orderedActions(meta: DynamicBlockMetadata): readonly DynamicAction[] {
  const list = meta.actions;
  const edges = meta.evaluationGraph?.edges ?? [];
  if (edges.length === 0) return list;

  // Tier 1 evaluation order: edges list dictates sequence. Preserve
  // declared order of edges, then append any actions absent from the
  // edge list (malformed graph protection) in declaration order.
  const byId = new Map(list.map((a) => [a.id, a] as const));
  const sorted: DynamicAction[] = [];
  const seen = new Set<string>();
  for (const edge of edges) {
    const action = byId.get(edge.actionId);
    if (action && !seen.has(action.id)) {
      sorted.push(action);
      seen.add(action.id);
    }
  }
  for (const a of list) {
    if (!seen.has(a.id)) {
      sorted.push(a);
      seen.add(a.id);
    }
  }
  return sorted;
}

/**
 * Apply a set of RuntimeValueUpdate to the parameter array, producing a
 * new array with the updated currents written in. Unknown parameter ids
 * or property keys are silently skipped (defensive). Updates to Lookup
 * parameters are allowed (setting currentLookupRowId).
 */
function applyRuntimeValueUpdates(
  parameters: readonly DynamicParameter[],
  updates: readonly RuntimeValueUpdate[],
): DynamicParameter[] {
  if (updates.length === 0) return parameters.slice();
  const byId = new Map(parameters.map((p) => [p.id, p] as const));
  for (const u of updates) {
    const p = byId.get(u.parameterId);
    if (!p) continue;
    switch (u.property) {
      case "currentDistance":
        if (
          (p.kind === "linear" || p.kind === "polar") &&
          typeof u.value === "number"
        ) {
          byId.set(p.id, { ...p, currentDistance: u.value });
        }
        break;
      case "currentAngle":
        if (
          (p.kind === "polar" || p.kind === "rotation") &&
          typeof u.value === "number"
        ) {
          byId.set(p.id, { ...p, currentAngle: u.value });
        }
        break;
      case "currentX":
        if (
          (p.kind === "point" || p.kind === "xy") &&
          typeof u.value === "number"
        ) {
          if (p.kind === "point") {
            byId.set(p.id, {
              ...p,
              currentPoint: { ...p.currentPoint, x: u.value },
            });
          } else {
            byId.set(p.id, { ...p, currentX: u.value });
          }
        }
        break;
      case "currentY":
        if (
          (p.kind === "point" || p.kind === "xy") &&
          typeof u.value === "number"
        ) {
          if (p.kind === "point") {
            byId.set(p.id, {
              ...p,
              currentPoint: { ...p.currentPoint, y: u.value },
            });
          } else {
            byId.set(p.id, { ...p, currentY: u.value });
          }
        }
        break;
      case "currentValue":
        if (p.kind === "flip" && typeof u.value === "boolean") {
          byId.set(p.id, { ...p, currentValue: u.value });
        }
        break;
      case "currentStateId":
        if (p.kind === "visibility" && typeof u.value === "string") {
          const match =
            p.states.find((s) => s.id === u.value) ??
            p.states.find((s) => s.name === u.value);
          if (match) byId.set(p.id, { ...p, currentStateId: match.id });
        }
        break;
      default:
        // Unknown property key — skip.
        break;
    }
  }
  // Preserve original order.
  return parameters.map((p) => byId.get(p.id) ?? p);
}

/* ── main ──────────────────────────────────────────────────────── */

export function evaluateBlock(input: EvaluateBlockInput): EvaluateBlockResult {
  const { blockDef, instance } = input;
  const sourceMeta = input.dynamicMetadata ?? blockDef.dynamicMetadata;

  // DYN-3 cycle detection — reject when the current blockDef name already
  // appears in the parent chain (direct or transitive cycle). Also
  // enforce MAX_NESTED_DYNAMIC_DEPTH to guard against pathological
  // unbounded recursion in malformed metadata.
  const parentChain: readonly string[] = input.parentChain ?? [];
  if (parentChain.length > MAX_NESTED_DYNAMIC_DEPTH) {
    return {
      entities: blockDef.entities as ReadonlyArray<
        EntityLike & { readonly id: string }
      >,
      runtimeValues: new Map(),
      errors: [
        `Nested dynamic block depth exceeded (> ${MAX_NESTED_DYNAMIC_DEPTH}); chain=[${parentChain.join(" → ")} → ${blockDef.name}]`,
      ],
      computedProperties: {},
    };
  }
  if (parentChain.includes(blockDef.name)) {
    return {
      entities: blockDef.entities as ReadonlyArray<
        EntityLike & { readonly id: string }
      >,
      runtimeValues: new Map(),
      errors: [
        `Nested dynamic block cycle detected: ${[...parentChain, blockDef.name].join(" → ")}`,
      ],
      computedProperties: {},
    };
  }

  // Static block fast path — no parameter math, no filtering.
  if (!sourceMeta) {
    return {
      entities: blockDef.entities as ReadonlyArray<
        EntityLike & { readonly id: string }
      >,
      runtimeValues: new Map(),
      errors: [],
      computedProperties: {},
    };
  }

  // 1. Overlay instance state onto metadata (does not mutate source).
  let meta = applyInstanceParameters(
    sourceMeta,
    instance.customProperties ?? {},
    instance.visibilityState,
  );

  // 2. Resolve parameter runtime values.
  let runtimeValues = new Map<string, ParameterRuntimeValue>();
  for (const p of meta.parameters) {
    const pStart = profilerSink?.parameterStart(p.id);
    runtimeValues.set(p.id, resolveParameterRuntimeValue(p));
    if (pStart !== undefined) profilerSink?.parameterEnd(p.id, pStart);
  }

  const errors: string[] = [];

  // 3. Lookup actions run first — they can cascade parameter updates.
  //    Run all lookups, collect updates, apply them, then re-resolve the
  //    runtime values of affected parameters. Single pass is enough for
  //    Tier 2 (no nested lookups-feeding-lookups scenario is authored in
  //    standard DXF; DYN-3 chain actions revisit this).
  const actionsAll = meta.actions;
  const lookupActions = actionsAll.filter((a) => a.kind === "lookup");
  const otherActions = actionsAll.filter((a) => a.kind !== "lookup");

  const accumulatedUpdates: RuntimeValueUpdate[] = [];
  if (lookupActions.length > 0) {
    const lookupCtx: ActionContext = {
      runtimeValues,
      parameters: meta.parameters,
      lookupTables: meta.lookupTables ?? [],
    };
    for (const la of lookupActions) {
      const aStart = profilerSink?.actionStart(la.id);
      const result = applyAction(la, [], lookupCtx);
      if (aStart !== undefined) profilerSink?.actionEnd(la.id, aStart);
      if (result.errors.length > 0) errors.push(...result.errors);
      if (result.runtimeValueUpdates && result.runtimeValueUpdates.length > 0) {
        accumulatedUpdates.push(...result.runtimeValueUpdates);
      }
    }
    if (accumulatedUpdates.length > 0) {
      const nextParams = applyRuntimeValueUpdates(
        meta.parameters,
        accumulatedUpdates,
      );
      meta = { ...meta, parameters: nextParams };
      // Re-resolve runtime values — only the affected subset really
      // changes, but re-resolving all keeps the code simple and cheap.
      runtimeValues = new Map<string, ParameterRuntimeValue>();
      for (const p of meta.parameters) {
        runtimeValues.set(p.id, resolveParameterRuntimeValue(p));
      }
    }
  }

  // 4. Apply remaining (non-lookup) actions in evaluation-graph order.
  //
  // DYN-3 Batch C introduces CHAIN GROUPS on top of the pre-existing
  // "independent actions" model:
  //
  //   - Chain groups are ordered arrays of action ids declared in
  //     `meta.evaluationGraph.chainGroups`. Within a chain group the
  //     actions run sequentially and each action's output geometry is
  //     fed as the input of the next action. This is how AutoCAD
  //     composes multi-stage transforms (e.g. a Linear parameter
  //     driving a Stretch followed by a Scale on the same selection).
  //
  //   - Actions NOT referenced by any chain group keep their
  //     pre-DYN-3 semantics — each acts independently on the original
  //     (post-lookup) entity set and writes back via id, so disjoint
  //     selections compose naturally.
  //
  // Execution order: chain groups first (preserving the chainGroups
  // authoring order), then independent actions in graph-edge order. A
  // chain-group member always also appears in `orderedNonLookup`, so
  // when we iterate independent actions we must skip those already
  // processed by a chain group.
  const ctx: ActionContext = {
    runtimeValues,
    parameters: meta.parameters,
    lookupTables: meta.lookupTables ?? [],
  };

  // Build a mutable id→entity map so actions operating on disjoint
  // selections don't clobber each other and order is preserved on the
  // final reconstruction below. Array actions may introduce additional
  // copies — those are tracked in `extras`.
  const byId = new Map<string, EntityLike & { id: string }>();
  for (const e of blockDef.entities) {
    byId.set(e.id, e as EntityLike & { id: string });
  }
  const extras: Array<EntityLike & { id: string }> = [];

  // Preserve graph-ordered sequence for non-lookup actions.
  const orderedAll = orderedActions(meta);
  const orderedNonLookup = orderedAll.filter((a) => a.kind !== "lookup");
  // Include any otherActions not already ordered (malformed graphs) so
  // nothing is silently dropped.
  const seen = new Set(orderedNonLookup.map((a) => a.id));
  for (const a of otherActions) {
    if (!seen.has(a.id)) {
      orderedNonLookup.push(a);
      seen.add(a.id);
    }
  }

  // ── Chain groups first (DYN-3 Batch C) ──
  const chainGroups = meta.evaluationGraph?.chainGroups ?? [];
  const actionsInChain = new Set<string>();
  const actionById = new Map(orderedNonLookup.map((a) => [a.id, a] as const));

  for (const group of chainGroups) {
    // Pipeline state: groupEntities is the ordered working copy that
    // each action in the group transforms in sequence.
    const groupEntities: Array<EntityLike & { id: string }> = [];
    const groupIndex = new Map<string, number>();
    for (const orig of blockDef.entities) {
      const current =
        byId.get(orig.id) ?? (orig as EntityLike & { id: string });
      groupIndex.set(orig.id, groupEntities.length);
      groupEntities.push(current);
    }
    const groupExtras: Array<EntityLike & { id: string }> = [];

    for (const actionId of group) {
      const action = actionById.get(actionId);
      if (!action) continue; // malformed group — skip silently
      actionsInChain.add(actionId);
      const selection: Array<EntityLike & { id: string }> = [];
      for (const selId of action.selectionEntityIds) {
        const idx = groupIndex.get(selId);
        if (idx !== undefined) {
          const e = groupEntities[idx];
          if (e) selection.push(e);
        }
      }
      if (selection.length === 0) continue;
      const aStart = profilerSink?.actionStart(action.id);
      const result = applyAction(action, selection, ctx);
      if (aStart !== undefined) profilerSink?.actionEnd(action.id, aStart);
      if (result.errors.length > 0) errors.push(...result.errors);

      if (action.kind === "array") {
        const resultEntities = result.entities;
        if (resultEntities.length === selection.length) {
          for (let i = 0; i < selection.length; i++) {
            const origId = selection[i]!.id;
            const targetIdx = groupIndex.get(origId);
            if (targetIdx !== undefined) {
              groupEntities[targetIdx] = resultEntities[i] as EntityLike & {
                id: string;
              };
            }
          }
        } else {
          const perInput = resultEntities.length / selection.length;
          for (let i = 0; i < selection.length; i++) {
            const origId = selection[i]!.id;
            const targetIdx = groupIndex.get(origId);
            for (let j = 0; j < perInput; j++) {
              const copy = resultEntities[i * perInput + j];
              if (!copy) continue;
              if (j === 0 && targetIdx !== undefined) {
                groupEntities[targetIdx] = copy as EntityLike & { id: string };
              } else {
                groupExtras.push({
                  ...(copy as EntityLike & { id?: string }),
                  id: `${origId}#arr${j}`,
                  // GitHub Codex review round 2, P2 — stamp UNAMBIGUOUS
                  // provenance at creation time (visibility.ts module doc
                  // "Provenance marker, not id-string guessing"): this
                  // object IS a synthetic copy of `origId`, regardless of
                  // whether its own computed `.id` happens to collide with
                  // an unrelated AUTHORED entity's id.
                  [ARRAY_COPY_SOURCE_MARKER]: origId,
                } as EntityLike & { id: string });
              }
            }
          }
        }
      } else {
        // Move / Stretch / Rotate / Scale / Flip — 1:1 replacement.
        for (let i = 0; i < selection.length; i++) {
          const origId = selection[i]!.id;
          const replacement = result.entities[i];
          const targetIdx = groupIndex.get(origId);
          if (replacement && targetIdx !== undefined) {
            groupEntities[targetIdx] = replacement as EntityLike & {
              id: string;
            };
          }
        }
      }
    }

    // Commit the group result back to the shared byId map. Extras are
    // appended to the top-level `extras` list so they survive past the
    // chain pipeline.
    for (const orig of blockDef.entities) {
      const idx = groupIndex.get(orig.id);
      if (idx === undefined) continue;
      const next = groupEntities[idx];
      if (next) byId.set(orig.id, next);
    }
    extras.push(...groupExtras);
  }

  // ── Independent actions — skip ones already processed by chain groups ──
  for (const action of orderedNonLookup) {
    if (actionsInChain.has(action.id)) continue;
    const selection: Array<EntityLike & { id: string }> = [];
    for (const id of action.selectionEntityIds) {
      const e = byId.get(id);
      if (e) selection.push(e);
    }
    if (selection.length === 0) continue;
    const aStart = profilerSink?.actionStart(action.id);
    const result = applyAction(action, selection, ctx);
    if (aStart !== undefined) profilerSink?.actionEnd(action.id, aStart);
    if (result.errors.length > 0) errors.push(...result.errors);

    if (action.kind === "array") {
      // Array replaces each selection entity with N copies. Per-input
      // the first copy stays in place (reuse original id), additional
      // copies land in `extras` with synthetic ids so the byId map does
      // not collide.
      const resultEntities = result.entities;
      if (resultEntities.length === selection.length) {
        // count === 1 → no copies created; treat as replace-in-place.
        for (let i = 0; i < selection.length; i++) {
          const originalId = selection[i]!.id;
          byId.set(
            originalId,
            resultEntities[i] as EntityLike & { id: string },
          );
        }
      } else {
        const perInput = resultEntities.length / selection.length;
        for (let i = 0; i < selection.length; i++) {
          const originalId = selection[i]!.id;
          for (let j = 0; j < perInput; j++) {
            const copy = resultEntities[i * perInput + j];
            if (!copy) continue;
            if (j === 0) {
              byId.set(originalId, copy as EntityLike & { id: string });
            } else {
              extras.push({
                ...(copy as EntityLike & { id?: string }),
                id: `${originalId}#arr${j}`,
                // GitHub Codex review round 2, P2 — see the chain-group
                // array branch's identical marker stamp above.
                [ARRAY_COPY_SOURCE_MARKER]: originalId,
              } as EntityLike & { id: string });
            }
          }
        }
      }
    } else {
      // Move / Stretch / Rotate / Scale / Flip — 1:1 replacement.
      for (let i = 0; i < selection.length; i++) {
        const originalId = selection[i]!.id;
        const replacement = result.entities[i];
        if (replacement) {
          byId.set(originalId, replacement as EntityLike & { id: string });
        }
      }
    }
  }

  // Reconstruct entities in original declaration order, plus extras.
  const afterActions: Array<EntityLike & { id: string }> = [];
  for (const original of blockDef.entities) {
    const next = byId.get(original.id);
    afterActions.push(next ?? (original as EntityLike & { id: string }));
  }
  afterActions.push(...extras);

  // 4b. DYN-5: constraint-based stretch fallback for action-less
  //     dimensional constraints. When a constraint-horizontal /
  //     constraint-vertical parameter has NO action linked to it (the
  //     ibeam / GOST-profile case: 14 constraint params, zero actions),
  //     the pre-DYN-5 evaluator silently ignored changes in
  //     currentDistance and rendered defaults. Here we apply an axial
  //     scale anchored at pointA of every such parameter whose runtime
  //     distance diverges from the default.
  //
  //     Action-driven parameters are intentionally skipped — the
  //     regular action pass above owns their transformation, so a
  //     double-apply would misrender the block.
  // DYN-5 debug — expose incoming customProperties too
  (globalThis as unknown as { __DYN5_CUSTOMPROPS__?: unknown }).__DYN5_CUSTOMPROPS__ =
    instance.customProperties ?? null;
  const stretchResult = applyConstraintStretch({
    parameters: meta.parameters,
    sourceParameters: sourceMeta.parameters,
    actions: meta.actions,
    entities: afterActions,
  });
  const afterStretch: Array<EntityLike & { id: string }> =
    stretchResult.entities as Array<EntityLike & { id: string }>;

  // 5. Constraint solver (DYN-3 Batch B): if the metadata carries a
  //    geometric / dimensional constraint system, run LM to bring the
  //    post-action entities into full constraint satisfaction.
  //
  //    Constraint descriptors live either on the formal metadata (once
  //    DYN-3 Batch A lands schema fields for them) or — for forward-
  //    compat during parallel development — inside `futureExtensions`
  //    under the `geometricConstraints` / `dimensionalConstraints` keys.
  //    Both locations are checked; whichever is present wins.
  //
  //    DYN-5 follow-up: BPT-driven blocks expose their dimensional
  //    constraints as parameter points (pointA/pointB/vertex/center)
  //    rather than explicit entityIds. `collectDimensionalConstraints
  //    FromParameters` maps those back to (entityId, subselector) refs
  //    by coordinate match, producing descriptors the solver can consume.
  //
  //    GATE: the solver runs when at least one GEOMETRIC constraint is
  //    present OR — for purely-dimensional blocks — an anchor entity can
  //    be inferred from the constraint-parameter topology. A purely-
  //    dimensional system (N distance constraints, no coincident /
  //    perpendicular / parallel / fix) is translation-invariant; without
  //    an anchor the LM solver converges at an arbitrary rigid shift and
  //    the block drifts relative to its authored basepoint. With an
  //    anchor pinned (via `fixedEntityIds`) the solver can refine the
  //    DYN-5 axial result toward full constraint satisfaction while
  //    keeping the anchor vertex at its pre-solve coordinates.
  //
  //    `inferAnchorEntity` mirrors AutoCAD's "fix the constraint-graph
  //    root" heuristic: the pointA that never appears as any other
  //    constraint's pointB. Fallback = leftmost-bottommost resolvable
  //    pointA (matches the GOST "left edge at origin" convention for
  //    ibeam / channel / angle profiles). If neither finds an entity,
  //    the gate stays closed and the DYN-5 axial heuristic remains
  //    authoritative — no regression for corner cases we can't anchor.
  let afterSolver = afterStretch;
  const geoConstraints = readGeometricConstraints(meta);
  const collectionWarnings: string[] = [];
  const explicitDimConstraints = readDimensionalConstraints(meta);
  // Derived dimensional constraints are collected regardless of gate
  // state: we need them both to run the solver AND to decide whether
  // anchor inference has anything to pin (a block with no resolvable
  // dimensional constraints is not worth anchor-gating).
  const derivedDimConstraints =
    geoConstraints.length > 0 || meta.parameters.length > 0
      ? collectDimensionalConstraintsFromParameters(
          meta.parameters,
          afterStretch,
          collectionWarnings,
        )
      : [];
  const dimConstraints: DimensionalConstraint[] = [
    ...explicitDimConstraints,
    ...derivedDimConstraints,
  ];
  const inferredAnchor =
    geoConstraints.length === 0 && derivedDimConstraints.length > 0
      ? inferAnchorEntity(meta.parameters, afterStretch)
      : null;
  const gateOpen =
    geoConstraints.length > 0 ||
    (inferredAnchor !== null && dimConstraints.length > 0);
  if (gateOpen) {
    const solveRes = solveBlockConstraints({
      entities: afterStretch,
      geometricConstraints: geoConstraints,
      dimensionalConstraints: dimConstraints,
      fixedEntityIds: inferredAnchor ? [inferredAnchor] : undefined,
      blockName: blockDef.name,
    });
    if (solveRes.cacheHit === false) profilerSink?.cacheMiss();
    if (solveRes.iterations !== undefined) {
      profilerSink?.solverReport(
        solveRes.iterations,
        solveRes.residualNorm ?? 0,
      );
    }
    if (solveRes.success) {
      afterSolver = solveRes.updatedEntities;
    } else if (solveRes.warnings.length > 0) {
      errors.push(...solveRes.warnings);
    }
    // Surface collection-stage warnings only when the solver ran, so
    // downstream consumers can correlate them with the solver's view of
    // the constraint system.
    if (collectionWarnings.length > 0) errors.push(...collectionWarnings);
  }

  // 6. Filter by visibility state (using post-overlay metadata so
  //    currentStateId is the instance's active state).
  // local Codex review round 2, P2, pre-PR — thread the STATIC authored
  // id set so `filterVisibleEntities` can distinguish a genuinely
  // authored id from a synthetic array-copy id that happens to collide
  // with the `#arrN` suffix pattern (see `visibility.ts`'s
  // `resolveVisibilityId` doc comment for the full rationale).
  const authoredIds = new Set(blockDef.entities.map((e) => e.id));
  const visible = filterVisibleEntities(afterSolver, meta, authoredIds);

  // 7. BTABLE formula evaluation (DYN-4.1). Scope = parameter runtime
  //    values (by parameter name) + static defaults already carried in
  //    entries + INSERT customProperties for string/boolean overrides.
  //    Formulas are ordered topologically; circular refs report as
  //    errors and the offending keys are omitted from computedProperties.
  const computedProperties: Record<string, FormulaValue> = {};
  const bpTable = meta.blockPropertiesTable;
  if (bpTable && bpTable.entries.length > 0) {
    const fStart = profilerSink?.formulaStart();
    const baseScope = buildFormulaScope(
      meta.parameters,
      runtimeValues,
      instance.customProperties ?? {},
    );
    const entries: FormulaEntry[] = bpTable.entries.map((e) => ({
      key: e.key,
      formula: e.formula,
      staticValue: e.defaultValue,
    }));
    const result = evaluateFormulaBatch(entries, baseScope);
    for (const [k, v] of Object.entries(result.values)) {
      computedProperties[k] = v;
    }
    if (result.errors.length > 0) errors.push(...result.errors);
    if (fStart !== undefined) profilerSink?.formulaEnd(fStart);
  }

  return { entities: visible, runtimeValues, errors, computedProperties };
}

/**
 * Build a flat variable scope for BTABLE formula evaluation. Exposes:
 *
 *   - every parameter's runtime value under the parameter NAME (so a
 *     formula can say `Width`, `Height`, `Angle1`);
 *   - for parameters with multi-field state (Polar/XY/Point/Rotation),
 *     also expose dotted keys: `Polar1.distance`, `Polar1.angle`,
 *     `XY1.x`, `XY1.y`, etc.;
 *   - INSERT per-instance customProperties overrides under their raw
 *     keys (users may type `prop.x` manually into customProperties);
 *   - Static BTABLE entry defaultValues as bootstrap scope — the batch
 *     evaluator overwrites them with formula output when available.
 *
 * Keys that don't participate in a formula are harmless — the scope is
 * only consulted on variable lookup.
 */
function buildFormulaScope(
  parameters: ReadonlyArray<
    import("@dxf-ai-editor/drawing-model").DynamicParameter
  >,
  runtimeValues: ReadonlyMap<string, ParameterRuntimeValue>,
  customProperties: Readonly<Record<string, string | number | boolean>>,
): FormulaScope {
  const scope: Record<string, FormulaValue> = {};
  for (const p of parameters) {
    const runtime = runtimeValues.get(p.id);
    if (!runtime) continue;
    switch (p.kind) {
      case "linear":
      case "constraint-horizontal":
      case "constraint-vertical":
      case "constraint-aligned":
        if (runtime.distance !== undefined) {
          scope[p.name] = runtime.distance;
          scope[`${p.name}.distance`] = runtime.distance;
        }
        break;
      case "rotation":
      case "alignment":
      case "constraint-angular":
        if (runtime.angle !== undefined) {
          scope[p.name] = runtime.angle;
          scope[`${p.name}.angle`] = runtime.angle;
        }
        break;
      case "polar":
        if (runtime.polar) {
          scope[`${p.name}.distance`] = runtime.polar.distance;
          scope[`${p.name}.angle`] = runtime.polar.angle;
        }
        break;
      case "point":
        if (runtime.point) {
          scope[`${p.name}.x`] = runtime.point.x;
          scope[`${p.name}.y`] = runtime.point.y;
        }
        break;
      case "xy":
        if (runtime.xy) {
          scope[`${p.name}.x`] = runtime.xy.x;
          scope[`${p.name}.y`] = runtime.xy.y;
        }
        break;
      case "flip":
        if (runtime.flipped !== undefined) {
          scope[p.name] = runtime.flipped;
        }
        break;
      case "visibility":
        if (runtime.stateId !== undefined) {
          scope[p.name] = runtime.stateId;
        }
        break;
      case "lookup":
        if (runtime.lookupRowId !== undefined) {
          scope[p.name] = runtime.lookupRowId;
        }
        break;
      case "constraint-radial":
        if (runtime.distance !== undefined) {
          scope[p.name] = runtime.distance;
          scope[`${p.name}.radius`] = runtime.distance;
        }
        break;
      case "constraint-diameter":
        if (runtime.distance !== undefined) {
          scope[p.name] = runtime.distance;
          scope[`${p.name}.diameter`] = runtime.distance;
        }
        break;
      case "basepoint":
        break;
      case "user":
        // DYN-4 Fix 3: User parameter — expose both the numeric shortcut
        // (when the backing ACDBASSOCVARIABLE is scalar) and the authored
        // defaultValue from the parameter (covers string-typed variables
        // and acts as a fallback for BLOCKPROPERTIESTABLE key lookups).
        if (runtime.distance !== undefined) {
          scope[p.name] = runtime.distance;
        } else if (p.defaultValue !== undefined) {
          scope[p.name] = p.defaultValue;
        }
        break;
    }
  }
  // Overlay INSERT customProperties — they may include user-authored
  // extras that don't match any parameter name but still belong to the
  // scope (e.g. non-dynamic string attributes).
  for (const [k, v] of Object.entries(customProperties)) {
    scope[k] = v;
  }
  return scope;
}

/**
 * Pull geometric constraints from metadata. Checks both the typed
 * schema field (when DYN-3 Batch A lands) and `futureExtensions` as a
 * forward-compat escape hatch.
 */
function readGeometricConstraints(
  meta: Record<string, unknown> & {
    futureExtensions?: Record<string, unknown>;
  },
): readonly GeometricConstraint[] {
  const typed = meta["geometricConstraints"];
  if (Array.isArray(typed)) return typed as readonly GeometricConstraint[];
  const ext = meta.futureExtensions;
  if (ext && Array.isArray(ext["geometricConstraints"])) {
    return ext["geometricConstraints"] as readonly GeometricConstraint[];
  }
  return [];
}

function readDimensionalConstraints(
  meta: Record<string, unknown> & {
    futureExtensions?: Record<string, unknown>;
  },
): readonly DimensionalConstraint[] {
  const typed = meta["dimensionalConstraints"];
  if (Array.isArray(typed)) return typed as readonly DimensionalConstraint[];
  const ext = meta.futureExtensions;
  if (ext && Array.isArray(ext["dimensionalConstraints"])) {
    return ext["dimensionalConstraints"] as readonly DimensionalConstraint[];
  }
  return [];
}
